import { useState } from 'react';
import { Check, Copy, Eye, EyeOff, Loader2 } from 'lucide-react';

/**
 * Un dato sensible: tapado hasta que alguien lo pide.
 *
 * El valor NO está en la página: lo trae `onMostrar` cuando se pulsa, y esa
 * petición queda registrada en la actividad de la ficha. Taparlo solo con CSS
 * habría dejado el DNI en el navegador de todas formas, y el registro se
 * saltaría sin más que mirar la pestaña de red.
 *
 * Todos los campos de una ficha se destapan a la vez —una sola lectura, un solo
 * apunte en la actividad— así que el estado vive en la pantalla y aquí solo se
 * pinta lo que toque.
 */
interface Props {
  /** El valor real, o `null` si aún no se ha pedido. */
  valor: string | null;
  /** Si la ficha tiene algo guardado en este campo. */
  tiene: boolean;
  onMostrar: () => Promise<void> | void;
  cargando?: boolean;
  /** Para el IBAN y el DNI, que se copian para pegarlos en otro sitio. */
  copiable?: boolean;
}

export default function CampoOculto({ valor, tiene, onMostrar, cargando, copiable }: Props) {
  const [copiado, setCopiado] = useState(false);

  if (!tiene) return <span className="text-sereno-dark/30">—</span>;

  const copiar = async () => {
    if (!valor) return;
    try {
      await navigator.clipboard.writeText(valor);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch {
      /* Sin portapapeles (http, permisos): el valor está a la vista igualmente. */
    }
  };

  if (valor === null) {
    return (
      <button
        type="button"
        onClick={() => void onMostrar()}
        disabled={cargando}
        className="inline-flex items-center gap-1.5 text-sereno-dark/50 hover:text-sereno-dark transition-colors disabled:opacity-50"
        title="Mostrar. Queda registrado en la actividad de la ficha."
      >
        <span className="font-mono tracking-widest select-none">••••••••</span>
        {cargando ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
      </button>
    );
  }

  return (
    <span className="inline-flex items-center gap-2">
      <span className="font-mono">{valor}</span>
      {copiable && (
        <button
          type="button"
          onClick={() => void copiar()}
          className="text-sereno-dark/30 hover:text-sereno-dark transition-colors"
          title="Copiar"
        >
          {copiado ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
        </button>
      )}
    </span>
  );
}

/** El botón que destapa todos los campos sensibles de la ficha a la vez. */
export function BotonSensibles({
  visible,
  cargando,
  onAlternar,
}: {
  visible: boolean;
  cargando: boolean;
  onAlternar: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onAlternar}
      disabled={cargando}
      className="inline-flex items-center gap-1.5 text-xs text-sereno-dark/50 hover:text-sereno-dark transition-colors disabled:opacity-50"
      title={visible ? 'Volver a ocultar' : 'Mostrar. Queda registrado en la actividad de la ficha.'}
    >
      {cargando ? (
        <Loader2 size={13} className="animate-spin" />
      ) : visible ? (
        <EyeOff size={13} />
      ) : (
        <Eye size={13} />
      )}
      {visible ? 'Ocultar datos sensibles' : 'Mostrar datos sensibles'}
    </button>
  );
}
